import styled from '@emotion/styled'
import { V2L2SmallModalRoot } from '../V2L2ModalRoot'
import { V2L2ModalTitlebar, V2L1ModalDismissButton } from '../V2ModalTitlebar'
import { V2L1ModalDetailLabel, V2L2ModalContent, V2L2ModalDetail } from '../V2ModalContent'
import { V2L2ModalActionbar } from '../V2L2ModalActionbar'
import { V2L1ReasonableButton } from '../../Button/V2L1ReasonableButton'
import { FusionColor } from '../../../../theme/FusionStyle'
import { V2L1ProgressBar, V2L1ProgressBarSegment } from '../../ProgressBar/V2L1ProgressBar'
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons'
import { SyncModalItem, useSyncModalItemSort } from './SyncModalItem'
import { V2L1SyncItemIndicator } from '../../Indicator/V2L1SyncItemIndicator'

const ProgressContainer = styled(V2L2ModalContent)({
	padding: '1rem 1rem 1.25rem',
	display: 'flex',
	flexDirection: 'column',
	rowGap: '0.5rem',
})

const ProgressLabelRow = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	fontSize: `${14/16}rem`,
})

const ProgressNumerator = styled.span(({theme}) => ({
	fontWeight: 600,
	color: theme.colors.blueMain,
}))

const SummaryContent = styled(V2L2ModalContent)({
	paddingBlock: 0,
})

const SummaryContentSection = styled.div({
	marginBlock: '1rem',
})

const SummaryItemsContainer = styled.div({
	marginTop: '0.5rem',
	paddingInline: '1rem',
	display: 'grid',
	gridTemplateColumns: 'max-content 1fr max-content 1fr',
	gap: '0.25rem 0.5rem',
	alignItems: 'center',
	fontSize: `${14/16}rem`,
	fontWeight: 600,
})

const ExplainerRoot = styled.div({
	fontWeight: 'normal',
	gridColumn: 'span 2',
	marginBlock: '-1rem',
	'p': {
		marginBlock: '1rem',
		'&:last-of-type': {
			marginBottom: '2rem',
		}
	},
})

interface V2L3SyncModalProps {
	syncing: boolean
	progressSegments: V2L1ProgressBarSegment[]
	progressDenominator: number
	notice: string|null
	downloadItems: SyncModalItem[]
	uploadItems: SyncModalItem[]
	canSyncNow: boolean
	onSyncClick?: React.MouseEventHandler<HTMLButtonElement>
	canDismiss: boolean
	onDismissClick?: React.MouseEventHandler<HTMLButtonElement>
}

export const V2L3SyncModal = ({syncing, progressDenominator, progressSegments, notice, downloadItems, uploadItems, canSyncNow, onSyncClick, canDismiss, onDismissClick}: V2L3SyncModalProps) => {
	// NOTE
	// these hooks are just a clean way of separating the fancy
	// sort and combine logic from the style and layout of the L3 component
	uploadItems = useSyncModalItemSort(uploadItems)
	downloadItems = useSyncModalItemSort(downloadItems)
	const hasItems = Boolean(notice || (downloadItems.length > 0) || (uploadItems.length > 0))
	return (
		<V2L2SmallModalRoot>
			<V2L2ModalTitlebar
				title="Cloud Sync"
				buttons={
					canDismiss ? <V2L1ModalDismissButton onClick={onDismissClick} /> : null
				}
			/>
			<ProgressContainer upper>
				<ProgressLabelRow>
					<V2L1ModalDetailLabel upper>Progress</V2L1ModalDetailLabel>
					<div><ProgressNumerator>{progressSegments.length}</ProgressNumerator> / {progressDenominator}</div>
				</ProgressLabelRow>
				<V2L1ProgressBar segments={progressSegments} />
			</ProgressContainer>
			{hasItems ? (
				<SummaryContent>
					{downloadItems.length > 0 ? (
						<SummaryContentSection>
							<V2L1ModalDetailLabel>Downloads</V2L1ModalDetailLabel>
							<SummaryItemsContainer>
								{downloadItems.map((item, idx) => (
									<V2L1SyncItemIndicator  key={idx} {...item} />
								))}
							</SummaryItemsContainer>
						</SummaryContentSection>
					) : null}
					{uploadItems.length > 0 ? (
						<SummaryContentSection>
							<V2L1ModalDetailLabel>Uploads</V2L1ModalDetailLabel>
							<SummaryItemsContainer>
								{uploadItems.map((item, idx) => (
									<V2L1SyncItemIndicator  key={idx} {...item} />
								))}
							</SummaryItemsContainer>
						</SummaryContentSection>
					) : null}
					{notice ? (
						<V2L2ModalDetail title="Notice">
							<ExplainerRoot>
								<p>{notice}</p>
							</ExplainerRoot>
						</V2L2ModalDetail>
					) : null}
				</SummaryContent>
			) : null}
			{(syncing || !canSyncNow) ? null : (
				<V2L2ModalActionbar>
					<V2L1ReasonableButton
						color={FusionColor.BlueMain}
						icon={faArrowsRotate}
						label="Sync Now"
						loading={syncing}
						onClick={onSyncClick}
					/>
				</V2L2ModalActionbar>
			)}
		</V2L2SmallModalRoot>
	)
}
